import React, { useRef, useState } from 'react';
import { Cropper } from 'react-advanced-cropper';
import { Button, Box, Modal, Typography, CircularProgress } from '@mui/material';
import toast from 'react-hot-toast';
import 'react-advanced-cropper/dist/style.css';
import 'react-advanced-cropper/dist/themes/bubble.css';
import { AddFileSanadByScan, DeleteFileSanad } from '../../services/sanad/sanad';
import { useTranslation } from 'react-i18next';

const CropImage = ({ imageSrc, onCropComplete, onCancel, maxUploadSize, usedFileSize }) => {
  const cropperRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [fileSize, setFileSize] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const handlePreviewCrop = async () => {
    if (!cropperRef.current) return;
    
    setIsLoading(true);
    try {
      const canvas = cropperRef.current.getCanvas();
      if (!canvas) throw new Error('Canvas not available');

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Blob creation failed'));
        }, 'image/jpeg', 0.7);
      });

      const fileSize = (blob.size / (1024 * 1024)).toFixed(2);
      const remainingSize = maxUploadSize - usedFileSize;

      if (parseFloat(fileSize) > remainingSize) {
        toast.error(t('Upload.SizeExceeded', { size: remainingSize.toFixed(2) }));
        return;
      }

      const formData = new FormData();
      formData.append('listpathfile', blob, 'cropped-image.jpg');
      
      const response = await AddFileSanadByScan(formData);
      
      setPreviewUrl(response.data.value);
      setShowPreview(true);
      setFileSize(fileSize);
      
    } catch (error) {
      toast.error(t('Upload.Uploadfail'));
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmCrop = () => {
    const fileData = {
      pathlist: previewUrl,
      size: fileSize
    };
    onCropComplete(fileData);
    setShowPreview(false);
    onCancel();
  };

  const handleBackClick = async () => {
    if (previewUrl) {
      try {
        const fileName = previewUrl.split("/").pop();
        await DeleteFileSanad(fileName);
        setShowPreview(false);
        setPreviewUrl(null);
        setFileSize(0);
      } catch (error) {
        console.error('Delete failed:', error);
      }
    } else {
      setShowPreview(false);
    }
  };

  const PreviewModal = () => (
    <Modal open={showPreview}   onClose={null}
    disableEscapeKeyDown>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: '500px',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('Upload.Preview')}</Typography>
        <img 
          alt="preview"
          src={previewUrl} 
          style={{ 
            width: '100%', 
            maxHeight: '500px', 
            objectFit: 'contain',
            marginBottom: '16px',
            border: '2px solid #e0e0e0',
            borderRadius: '4px'
          }} 
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" color="primary" onClick={handleConfirmCrop}>
            {t('Upload.Add')}
          </Button>
          <Button variant="outlined" color="primary" onClick={handleBackClick}>
            {t('Upload.Cancel')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );

  return (
    <>
      <Modal open={true} onClose={onCancel}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          height: '90%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{t('Upload.CropTitle')}</Typography>
          <Box sx={{ width: '100%', height: 'calc(100% - 120px)', mb: 2 }}>
            <Cropper
              src={imageSrc}
              ref={cropperRef}
              className="cropper"
              stencilProps={{
                grid: true,
              }}
              style={{ height: '100%' }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handlePreviewCrop}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Typography marginRight={2}>{t('Progress.Proccessing')}</Typography>
                  <CircularProgress size={24} color="inherit" /> 
                </>
              ) : (
                t('Upload.Confirm')
              )}
            </Button>
            <Button variant="outlined" color="primary" onClick={onCancel}>
              {t('Upload.Cancel')}
            </Button>
          </Box>
        </Box>
      </Modal>
      {showPreview && <PreviewModal />}
    </>
  );
};

export default CropImage;
